import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "Silky smooth Piechart transitions with React and D3.js",
  "description": "",
  "published": "2018-03-09T00:00:00.000Z",
  "redirect_from": ["/blog/silky-smooth-piechart-transitions-react-d3js/swizec/8258"],
  "categories": "d3, Front End, react, Technical",
  "hero": "../../../defaultHero.jpg"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2018/03/transition-pie.gif",
        "alt": "A pie streaming in some data"
      }}></img></p>
    <p>{`Today, I finally figured out how to build smooth D3 arc transitions. 🎉`}</p>
    <p>{`Got some help from Bostock's `}<a parentName="p" {...{
        "href": "http://bl.ocks.org/mbostock/5100636"
      }}>{`commented arc tween`}</a>{` block and Andy Shora's guide on `}<a parentName="p" {...{
        "href": "http://andyshora.com/tweening-shapes-paths-d3-js.html"
      }}>{`tweening custom shapes and paths in D3.js`}</a>{`.`}</p>
    <blockquote>
      <p parentName="blockquote">{`The single most important requirement to perform interpolation in D3.js is that the structure of A must match the structure of B.`}</p>
      <p parentName="blockquote">{`~ Andy Shora`}</p>
    </blockquote>
    <p>{`That quote made it click. D'oh! So obvious.`}</p>
    <h2 {...{
      "id": "why-arc-transitions-are-hard"
    }}>{`Why arc transitions are hard`}</h2>
    <p>{`You see, the problem with arc transitions is that their path definition has a funny shape. It looks like this:`}</p>
    <pre><code parentName="pre" {...{}}>{`M100.6434055594246,-99.8203632756589A8,8,0,0,1,112.2823856114007,-99.46188154973098A150,150,0,0,1,-104.56274177607584,107.54828233063364A8,8,0,0,1,-104.38099615277264,95.90520136696549L-64.39381262786019,59.38549403963366A8,8,0,0,1,-53.635344263429694,59.35696964757701A80,80,0,0,0,61.78081312913049,-50.82451307295977A8,8,0,0,1,62.30830828934212,-61.57007978883599Z
`}</code></pre>
    <p>{`If that doesn't make sense, don't worry. I can't read it either. Maybe `}<a parentName="p" {...{
        "href": "https://twitter.com/sarah_edo"
      }}>{`Sarah Drasner`}</a>{` can, she's an SVG goddess 🤔`}</p>
    <p>{`PS: she can 👇`}</p>
    <blockquote {...{
      "className": "twitter-tweet",
      "data-dnt": "true"
    }}><p parentName="blockquote" {...{
        "lang": "en",
        "dir": "ltr"
      }}>{`I can- I mean most of this is much easier to read with a .toFixed(0) or .toFixed(1), and I'll admit the As are much more of a pain than say, Q, which are my favorite.`}<br parentName="p"></br><br parentName="p"></br>{`If I wrote it by hand it would probably be more legible for you, too`}</p>{`— Sarah Drasner (@sarah_edo) `}<a parentName="blockquote" {...{
        "href": "https://twitter.com/sarah_edo/status/971840965488119809"
      }}>{`March 8, 2018`}</a></blockquote>
    <p>{`When you build a transition, you're trying to smoothly move from A to B. To get from `}<inlineCode parentName="p">{`0`}</inlineCode>{` to `}<inlineCode parentName="p">{`1`}</inlineCode>{`, you go through `}<inlineCode parentName="p">{`0.1`}</inlineCode>{`, `}<inlineCode parentName="p">{`0.2`}</inlineCode>{` and so on.`}</p>
    <p>{`But a path definition is more complex.`}</p>
    <p>{`You're dealing with a bunch of numbers that have to move just right. Change them all together, and funny things may happen like arcs flying around the screen.`}</p>
    <p>{`Or an error. 😬`}</p>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2018/03/naive-transition-d3-pie.gif",
        "alt": "Naively implemented transition. Notice the arcs change shape sometimes"
      }}></img></p>
    <p><img parentName="p" {...{
        "src": "/2b411e26e2545749bc89237f421c45a7/media-sDZhdjNDsIq1q-giphy.gif",
        "alt": null
      }}></img></p>
    <h2 {...{
      "id": "tweens-to-the-rescue"
    }}>{`Tweens to the rescue`}</h2>
    <p>{`Luckily, D3 lets us define custom transitions called tweens. To smoothly animate a piechart, we're going to build an `}<inlineCode parentName="p">{`arcTween`}</inlineCode>{`. Because piecharts are made of arcs.`}</p>
    <p>{`The idea is to move from blindly transitioning path definitions to transitioning angles on a pie slice. We're building a tween generator that takes some params and returns a tweening function.`}</p>
    <p>{`Tweening functions are what makes transitions work, by the way. They take an argument, `}<inlineCode parentName="p">{`t`}</inlineCode>{`, and return the value of your prop at that specific "time" of your transition.`}</p>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2018/03/transition-sketch.png",
        "alt": null
      }}></img></p>
    <p>{`Our tween generator is going to need:`}</p>
    <ol>
      <li parentName="ol"><inlineCode parentName="li">{`oldData`}</inlineCode>{`, the definition of our pie slice at the start of our transition`}</li>
      <li parentName="ol"><inlineCode parentName="li">{`newData`}</inlineCode>{`, the definition of our pie slice that we want to tween towards`}</li>
      <li parentName="ol"><inlineCode parentName="li">{`arc`}</inlineCode>{`, a `}<a parentName="li" {...{
          "href": "https://github.com/d3/d3-shape/blob/master/README.md#arcs"
        }}>{`D3 arc generator`}</a></li>
    </ol>
    <p>{`Both `}<inlineCode parentName="p">{`oldData`}</inlineCode>{` and `}<inlineCode parentName="p">{`newData`}</inlineCode>{` come from a `}<a parentName="p" {...{
        "href": "https://github.com/d3/d3-shape/blob/master/README.md#pies"
      }}>{`D3 pie generator`}</a>{`. Their `}<inlineCode parentName="p">{`startAngle`}</inlineCode>{` and `}<inlineCode parentName="p">{`endAngle`}</inlineCode>{` is what we're interested in.`}</p>
    <p>{`Our `}<inlineCode parentName="p">{`arcTween`}</inlineCode>{` function uses these to build a tween method that we then feed into `}<inlineCode parentName="p">{`attrTween`}</inlineCode>{`.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`// inspired from http://bl.ocks.org/mbostock/5100636
function arcTween(oldData, newData, arc) {
  const copy = { ...oldData };
  return function () {
    const interpolateStartAngle = d3.interpolate(
        oldData.startAngle,
        newData.startAngle
      ),
      interpolateEndAngle = d3.interpolate(oldData.endAngle, newData.endAngle);

    return function (t) {
      copy.startAngle = interpolateStartAngle(t);
      copy.endAngle = interpolateEndAngle(t);
      return arc(copy);
    };
  };
}
`}</code></pre>
    <p>{`We make a `}<inlineCode parentName="p">{`copy`}</inlineCode>{` of `}<inlineCode parentName="p">{`oldData`}</inlineCode>{` so we don't change input data by accident, then we return a function. This function creates two interpolators with `}<inlineCode parentName="p">{`d3.interpolate`}</inlineCode>{`.`}</p>
    <p>{`Each interpolator starts from an `}<inlineCode parentName="p">{`oldData`}</inlineCode>{` angle and moves towards a `}<inlineCode parentName="p">{`newData`}</inlineCode>{` angle. One for start, one for end.`}</p>
    <p>{`This function then returns our actual interpolation function. It takes the argument `}<inlineCode parentName="p">{`t`}</inlineCode>{`, feeds it into our two interpolators, adjusts values on the `}<inlineCode parentName="p">{`copy`}</inlineCode>{` object, feeds that into the `}<inlineCode parentName="p">{`arc`}</inlineCode>{` generator, and returns a new path definition.`}</p>
    <p><img parentName="p" {...{
        "src": "/93adb298ace578c16f01000a47c03f6c/media-3o7btPCcdNniyf0ArS-giphy.gif",
        "alt": null
      }}></img></p>
    <p>{`You use it like this 👇`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`// Piechart.js
d3.select(this.refs.elem)
  .transition()
  .duration(80)
  .attrTween("d", arcTween(this.state.d, newProps.d, this.arc))
  .on("end", () =>
    this.setState({
      d: newProps.d,
      pathD: this.arc(newProps.d),
    })
  );
`}</code></pre>
    <p>{`Select an element, a `}<inlineCode parentName="p">{`<path>`}</inlineCode>{`, start a transition, make it last `}<inlineCode parentName="p">{`80`}</inlineCode>{` milliseconds, `}<inlineCode parentName="p">{`attrTween`}</inlineCode>{` the path definition, `}<inlineCode parentName="p">{`d`}</inlineCode>{`, attribute using the tween returned from `}<inlineCode parentName="p">{`arcTween`}</inlineCode>{`.`}</p>
    <p>{`Better? 🤪`}</p>
    <p>{`Let's put it to use in a piechart. We're using `}<a parentName="p" {...{
        "href": "https://swizec.com/reactd3js"
      }}>{`React and D3`}</a>{` because React makes dataviz code easier to understand.`}</p>
    <p>{`We build our piechart from 2 components:`}</p>
    <ol>
      <li parentName="ol">{`Piechart – takes data, feeds into a `}<inlineCode parentName="li">{`d3.pie()`}</inlineCode>{` generator, renders a bunch of arcs in a loop`}</li>
      <li parentName="ol">{`Arc - takes data for an arc, feeds it into a `}<inlineCode parentName="li">{`d3.arc()`}</inlineCode>{` generator, renders a `}<inlineCode parentName="li">{`<path>`}</inlineCode>{` element, handles transitions`}</li>
    </ol>
    <p>{`You can see the `}<a parentName="p" {...{
        "href": "https://github.com/Swizec/react-d3-pie"
      }}>{`full code on GitHub`}</a></p>
    <h2 {...{
      "id": ""
    }}></h2>
    <p>{`The Piechart component itself is pretty simple. Takes some data, renders some arcs.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`// Piechart.js
class Piechart extends Component {
    pie = d3
        .pie()
        .value(d => d.amount)
        .sortValues(d => d.tag)
        .padAngle(0.005);

    render() {
        const { data, groupBy, x, y, color } = this.props;

        const _data = groupByFunc(data, groupBy);

        return (
            <g transform="{\`translate(\${x}," \${y})\`}="">
                {this.pie(_data).map((d, i) => (
                    <arc d={d} color={color(d)}>
                ))}
                <text x="0" textanchor="middle">
                    {data.length}
                </text>
                <text y="18" x="0" textanchor="middle">
                    datapoints
                </text>
            </arc></g>
        );
    }
}
`}</code></pre>
    <p>{`We define a pie generator with a value accessor `}<inlineCode parentName="p">{`d => d.amount`}</inlineCode>{` that sorts arcs by `}<inlineCode parentName="p">{`d.tag`}</inlineCode>{`, and adds a padding of `}<inlineCode parentName="p">{`0.005`}</inlineCode>{` between arcs. To learn more about how padding works, check out this wonderful `}<a parentName="p" {...{
        "href": "https://bl.ocks.org/mbostock/3e961b4c97a1b543fff2"
      }}>{`pie padding animation`}</a>{` by Mike Bostock.`}</p>
    <p>{`The `}<inlineCode parentName="p">{`render`}</inlineCode>{` method groups `}<inlineCode parentName="p">{`data`}</inlineCode>{` by a given `}<inlineCode parentName="p">{`groupBy`}</inlineCode>{` function, in our case by tag, then outputs a grouping element `}<inlineCode parentName="p">{`<g>`}</inlineCode>{` inside it.`}</p>
    <ol>
      <li parentName="ol">{`Loops through the output of `}<inlineCode parentName="li">{`this.pie(_data)`}</inlineCode>{` and renders an `}<inlineCode parentName="li">{`<Arc>`}</inlineCode>{` for each value`}</li>
      <li parentName="ol">{`Creates two `}<inlineCode parentName="li">{`<text>`}</inlineCode>{` nodes for the center of our piechart`}</li>
    </ol>
    <p>{`How data makes it into `}<inlineCode parentName="p">{`<Piechart>`}</inlineCode>{` is outside the scope of this tutorial. You can assume data comes as an array that changes every couple milliseconds. This triggers a re-render, which propagates into our `}<inlineCode parentName="p">{`<Arc>`}</inlineCode>{` components.`}</p>
    <p>{`You can read that code on GitHub in `}<a parentName="p" {...{
        "href": "https://github.com/Swizec/react-d3-pie/blob/master/src/App.js"
      }}>{`the App component`}</a>{`.`}</p>
    <h2 {...{
      "id": "-1"
    }}></h2>
    <p>{`Our `}<inlineCode parentName="p">{`<Piechart>`}</inlineCode>{` gets updated data every few milliseconds and re-renders. This change propagates into `}<inlineCode parentName="p">{`<Arc>`}</inlineCode>{` components via props.`}</p>
    <p>{`That means `}<inlineCode parentName="p">{`<Arc>`}</inlineCode>{` has to handle transitions. Pushing transitions into the `}<inlineCode parentName="p">{`<Arc>`}</inlineCode>{` component means we can preserve React's ideal of declarative rendering. Piechart just renders Arcs and gives them info. Arcs handle everything about rendering pie arcs. Even transitions.`}</p>
    <p>{`The general approach comes from my `}<a parentName="p" {...{
        "href": "https://swizec.com/reactd3js"
      }}>{`React+D3v4 book`}</a>{`:`}</p>
    <ol>
      <li parentName="ol">{`Move props into state`}</li>
      <li parentName="ol">{`Use state to render`}</li>
      <li parentName="ol">{`Transition raw attributes with D3`}</li>
      <li parentName="ol">{`Update state when transition ends`}</li>
    </ol>
    <p>{`The outline for our `}<inlineCode parentName="p">{`<Arc>`}</inlineCode>{` component looks like this 👇`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`// Piechart.js

class Arc extends Component {
    arc = d3
        .arc()
        .innerRadius(80)
        .outerRadius(150)
        .cornerRadius(8);

    constructor(props) {
        super(props);

        this.state = {
            color: props.color,
            origCol: props.color,
            d: props.d,
            pathD: this.arc(props.d)
        };
    }

    componentWillReceiveProps(newProps) {
        // transition, state update
    }

    // hover/unhover color changes via this.setState

    render() {
        const { color, pathD } = this.state;

        return (
            <path d={pathD} style={{ fill: color }} onmouseover={this.hover} onmouseout={this.unhover} ref="elem">
        );
    }
}
</path>
`}</code></pre>
    <p>{`Start with an `}<inlineCode parentName="p">{`arc`}</inlineCode>{` generator that takes data and returns path definitions. Ours has an innerRadius of `}<inlineCode parentName="p">{`80`}</inlineCode>{`, an outerRadius of `}<inlineCode parentName="p">{`150`}</inlineCode>{`, and rounded corners.`}</p>
    <p>{`In the `}<inlineCode parentName="p">{`constructor`}</inlineCode>{`, we copy important props to `}<inlineCode parentName="p">{`this.state`}</inlineCode>{`. A good choice are props that we later intend to change. Color on hover/unhover, and `}<inlineCode parentName="p">{`d`}</inlineCode>{` and `}<inlineCode parentName="p">{`pathD`}</inlineCode>{` on input data changes.`}</p>
    <p><inlineCode parentName="p">{`pathD`}</inlineCode>{` is the part we're going to transition. It's the output of calling `}<inlineCode parentName="p">{`this.arc`}</inlineCode>{` on `}<inlineCode parentName="p">{`this.state.d`}</inlineCode>{`.`}</p>
    <p><inlineCode parentName="p">{`componentWillReceiveProps`}</inlineCode>{` is where that transition is going to happen.`}</p>
    <p><inlineCode parentName="p">{`render`}</inlineCode>{` doesn't do much. It outputs a `}<inlineCode parentName="p">{`<path>`}</inlineCode>{` element with a ref of `}<inlineCode parentName="p">{`elem`}</inlineCode>{`. It also defines mouse event handlers.`}</p>
    <h2 {...{
      "id": "adding-a-d3-transition-to-a-react-component"
    }}>{`Adding a D3 transition to a React component`}</h2>
    <p>{`Now that `}<inlineCode parentName="p">{`<Arc>`}</inlineCode>{` is rendering from state, we can use D3 transitions to make updates smoother. That happens inside `}<inlineCode parentName="p">{`componentWillReceiveProps`}</inlineCode>{`.`}</p>
    <p>{`Our goal is to take the new props, use D3 to transition appropriate attributes on the base DOM node, then update state with the new values to ensure React's engine knows what's going on.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`// Piechart.js in <arc>

    componentWillReceiveProps(newProps) {
        this.setState({
            color: newProps.color
        });

        d3
            .select(this.refs.elem)
            .transition()
            .duration(80)
            .attrTween("d", arcTween(this.state.d, newProps.d, this.arc))
            .on("end", () =>
                this.setState({
                    d: newProps.d,
                    pathD: this.arc(newProps.d)
                })
            );
    }
</arc>
`}</code></pre>
    <p>{`We update color state right away. This triggers a render, eventually.`}</p>
    <p>{`Then we start an 80 millisecond transition that uses the `}<inlineCode parentName="p">{`arcTween`}</inlineCode>{` generator we built earlier. When the transition ends, we update React state with the new values for `}<inlineCode parentName="p">{`d`}</inlineCode>{` and `}<inlineCode parentName="p">{`pathD`}</inlineCode>{`. This triggers another render.`}</p>
    <p>{`That might sound like a lot of renders, but it works okay. Don't worry about wasting resources, you're re-rendering a single `}<inlineCode parentName="p">{`<path>`}</inlineCode>{` element. `}<a parentName="p" {...{
        "href": "https://swizec.com/blog/building-interactive-dom-benchmark-preliminary-results/swizec/8219"
      }}>{`The DOM is pretty fast`}</a>{` ☺️`}</p>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2018/03/transition-pie-1.gif",
        "alt": "Our pie looks nice and smooth."
      }}></img></p>
    <h2 {...{
      "id": "recap"
    }}>{`Recap`}</h2>
    <p>{`You learned how to build a silky animated smooth piechart with React and D3! Yay! `}{`\\`}{`o/`}</p>
    <p>{`In a nutshell 👇`}</p>
    <ul>
      <li parentName="ul">{`Use custom tweens to transition complex shapes`}</li>
      <li parentName="ul">{`Render from state`}</li>
      <li parentName="ul">{`Always update state after transitions end`}</li>
    </ul>
    <p>{`🤘 Party hard 🤘`}</p>
    <p>{`To learn more about using React and D3 to write declarative data visualization code, read my book `}<a parentName="p" {...{
        "href": "https://swizec.com/reactd3js"
      }}>{`React+D3v4`}</a>{`. I'm probably adding this as a new chapter.`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      